import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["zipCode", "city", "state", "addressLine", "addressNumber", "status"]

  connect() {
    this.lookupTimeout = null
    this.abortController = null
    this.lastFetchedZipCode = null
  }

  disconnect() {
    clearTimeout(this.lookupTimeout)
    this.abortController?.abort()
  }

  scheduleLookup() {
    this.formatZipCode()

    if (!this.hasCompleteZipCode()) {
      clearTimeout(this.lookupTimeout)
      this.abortController?.abort()
      this.lastFetchedZipCode = null
      this.resetStatus()
      return
    }

    clearTimeout(this.lookupTimeout)
    this.lookupTimeout = setTimeout(() => this.lookup(), 300)
  }

  lookupOnBlur() {
    this.formatZipCode()
    clearTimeout(this.lookupTimeout)

    if (this.hasCompleteZipCode()) {
      this.lookup()
    }
  }

  async lookup() {
    clearTimeout(this.lookupTimeout)

    const zipCode = this.normalizedZipCode()

    if (zipCode.length !== 8 || zipCode === this.lastFetchedZipCode) {
      return
    }

    this.abortController?.abort()
    this.abortController = new AbortController()

    this.showStatus("Buscando CEP...", false)

    try {
      const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`, {
        signal: this.abortController.signal
      })

      if (!response.ok) {
        throw new Error("lookup_failed")
      }

      const data = await response.json()

      if (data.erro) {
        this.lastFetchedZipCode = null
        this.showStatus("CEP não encontrado. Preencha o endereço manualmente.", true)
        return
      }

      this.addressLineTarget.value = data.logradouro || ""
      this.cityTarget.value = data.localidade || ""
      this.stateTarget.value = data.uf || ""
      this.lastFetchedZipCode = zipCode

      this.showStatus("Endereço preenchido automaticamente.", false)

      if (!this.addressNumberTarget.value.trim()) {
        this.addressNumberTarget.focus()
      }
    } catch (error) {
      if (error.name === "AbortError") {
        return
      }

      this.lastFetchedZipCode = null
      this.showStatus("Não foi possível consultar o CEP agora.", true)
    }
  }

  formatZipCode() {
    const digits = this.normalizedZipCode()

    if (digits.length > 5) {
      this.zipCodeTarget.value = `${digits.slice(0, 5)}-${digits.slice(5)}`
    } else {
      this.zipCodeTarget.value = digits
    }
  }

  normalizedZipCode() {
    return this.zipCodeTarget.value.replace(/\D/g, "").slice(0, 8)
  }

  hasCompleteZipCode() {
    return this.normalizedZipCode().length === 8
  }

  resetStatus() {
    if (!this.hasStatusTarget) {
      return
    }

    this.statusTarget.textContent = ""
    this.statusTarget.classList.add("hidden")
    this.statusTarget.classList.remove("text-red-600")
    this.statusTarget.classList.add("text-slate-500")
  }

  showStatus(message, isError) {
    if (!this.hasStatusTarget) {
      return
    }

    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("hidden", "text-red-600", "text-slate-500")
    this.statusTarget.classList.add(isError ? "text-red-600" : "text-slate-500")
  }
}
